'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import TestimonialCard from './TestimonialCard';
import { useLanguage } from '@/contexts/LanguageContext';

interface Testimonial {
  id: number;
  name: string;
  role: { en: string; ar: string };
  image: string;
  alt: string;
  rating: number;
  comment: { en: string; ar: string };
  date: { en: string; ar: string };
}

const TestimonialsSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { language, t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Ahmed Al-Rashid",
    role: { en: 'Villa Owner, Farwaniya', ar: 'مالك فيلا، الفروانية' },
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4710b43-1763294093140.png",
    alt: "Middle-aged Arab man with short black hair wearing white traditional dress smiling confidently",
    rating: 5,
    comment: { en: 'Almanara made our villa move completely stress-free. The team was professional, careful with our belongings, and finished ahead of schedule. Highly recommended!', ar: 'جعلت المنارة نقل فيلتنا خالياً من الإجهاد. كان الفريق محترفاً وحريصاً على ممتلكاتنا، وأنهوا العمل قبل الموعد. موصى به بشدة!' },
    date: { en: 'December 2025', ar: 'ديسمبر ٢٠٢٥' }
  },
  {
    id: 2,
    name: "Sarah Mitchell",
    role: { en: 'Expatriate Family', ar: 'عائلة مغتربة' },
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc23a89f-1763301305375.png",
    alt: "Young woman with blonde hair in casual blue shirt smiling warmly at camera",
    rating: 5,
    comment: { en: 'Moving internationally is challenging, but Almanara\'s team made it seamless. They handled everything with care and kept us informed throughout the entire process.', ar: 'النقل الدولي محدياً، لكن فريق المنارة جعله سلساً. تعاملوا مع كل شيء بعناية وأبقونا على اطلاع طوال العملية بأكملها.' },
    date: { en: 'November 2025', ar: 'نوفمبر ٢٠٢٥' }
  },
  {
    id: 3,
    name: "Khalid Al-Mutairi",
    role: { en: 'Business Owner', ar: 'صاحب عمل' },
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16679c0dc-1763293361742.png",
    alt: "Professional Arab businessman in navy suit with trimmed beard looking confident",
    rating: 5,
    comment: { en: 'We relocated our entire office with Almanara. Zero downtime, professional service, and excellent value. They truly understand business needs.', ar: 'نقلنا مكتبنا بالكامل مع المنارة. لا توقف، خدمة احترافية، وقيمة ممتازة. يفهمون احتياجات الأعمال حقاً.' },
    date: { en: 'January 2026', ar: 'يناير ٢٠٢٦' }
  },
  {
    id: 4,
    name: "Fatima Hassan",
    role: { en: 'Apartment Resident', ar: 'ساكنة شقة' },
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16f1d13d5-1763294375575.png",
    alt: "Young Arab woman with long dark hair wearing professional attire smiling brightly",
    rating: 5,
    comment: { en: 'The packing service was exceptional! Every item arrived safely at my new apartment. The team was respectful, efficient, and very professional.', ar: 'كانت خدمة التعبئة استثنائية! وصل كل عنصر بأمان إلى شقتي الجديدة. كان الفريق محترماً وفعالاً ومحترفاً جداً.' },
    date: { en: 'October 2025', ar: 'أكتوبر ٢٠٢٥' }
  }];


  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full mb-4">
              <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" className="text-primary" />
              <span className="text-sm font-body font-semibold text-primary">Customer Reviews</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-text-secondary font-body">
              Real experiences from families and businesses who trusted Almanara with their moves.
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {testimonials.slice(0, 2).map((testimonial) =>
              <TestimonialCard
                key={testimonial.id}
                name={testimonial.name}
                role={testimonial.role.en}
                image={testimonial.image}
                alt={testimonial.alt}
                rating={testimonial.rating}
                comment={testimonial.comment.en}
                date={testimonial.date.en} />

              )}
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-16 lg:py-24 bg-muted" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full mb-4">
            <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-body font-semibold text-primary">{t({ en: 'Customer Reviews', ar: 'آراء العملاء' })}</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
            {t({ en: 'What Our Customers Say', ar: 'ماذا يقول عملاؤنا' })}
          </h2>
          <p className="text-lg text-text-secondary font-body">
            {t({ en: 'Real experiences from families and businesses who trusted Almanara with their moves.', ar: 'تجارب حقيقية من عائلات وشركات وثقت بالمنارة في نقلهم.' })}
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto relative">
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.slice(currentIndex, currentIndex + 2).map((testimonial) =>
            <TestimonialCard
              key={testimonial.id}
              name={testimonial.name}
              role={testimonial.role[language]}
              image={testimonial.image}
              alt={testimonial.alt}
              rating={testimonial.rating}
              comment={testimonial.comment[language]}
              date={testimonial.date[language]} />

            )}
          </div>
          
          <div className="flex items-center justify-center space-x-4 mt-8">
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-white shadow-soft hover:shadow-elevated transition-all duration-base flex items-center justify-center"
              aria-label="Previous testimonials">

              <Icon name="ChevronLeftIcon" size={24} variant="outline" className="text-primary" />
            </button>
            
            <div className="flex items-center space-x-2">
              {testimonials.map((testimonial, index) =>
              <button
                key={testimonial.id}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-base ${
                index === currentIndex ? 'bg-primary w-8' : 'bg-gray-300'}`
                }
                aria-label={`Go to testimonial ${index + 1}`} />

              )}
            </div>
            
            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-white shadow-soft hover:shadow-elevated transition-all duration-base flex items-center justify-center"
              aria-label="Next testimonials">

              <Icon name="ChevronRightIcon" size={24} variant="outline" className="text-primary" />
            </button>
          </div>
          
          <div className="text-center mt-8">
            <Link
              href="/contact"
              className="inline-flex items-center space-x-2 text-primary font-body font-semibold hover:text-secondary transition-colors duration-base">

              <span>{t({ en: 'Read More Reviews', ar: 'اقرأ المزيد من التقييمات' })}</span>
              <Icon name="ArrowRightIcon" size={20} variant="outline" />
            </Link>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;